import React, { useEffect } from 'react';
import FormModal from '../../components/Modals/FormModal';
import SelectField from '../../components/Forms/Input/SelectField';

import { useEntityType } from '../../hooks/useEntityType';
import { useTaxpayerCategory } from '../../hooks/useTaxpayerCategory';
import { useEconomicActivities } from '../../hooks/useEconomicActivities';
import { useLocation } from '../../hooks/useLocation';
import { useDocumentIdentificationType } from '../../hooks/documentIdentificationType/useDocumentIdentificationType';
import SupplierNaturalForm from './forms/SupplierNaturalForm';
import SupplierJuridicoForm from './forms/SupplierJuridicoForm';
import SupplierSubjectExcludedForm from './forms/SupplierSubjectExcludedForm';
import { useSupplier } from '../../hooks/useSupplier';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: () => void;
}

const SupplierEditPopup: React.FC<Props> = ({ isOpen, onClose, onSubmit }) => {
  const {
    form,
    isSubmitting,
    updateFormField,
    // changeEntityType,
    saveSupplier,
  } = useSupplier();

  // Dropdowns
  const { fetchEntityTypeDropdown, entityTypeDropdown } = useEntityType();
  const { fetchTaxpayerCategoryDropdown, taxpayerCategoryDropdown } =
    useTaxpayerCategory();
  const { fetchEconomicActivitiesDropdown, economicActivitiesDropdown } =
    useEconomicActivities();
  const {
    fetchDocumentIdentificationTypeDropdown,
    documentIdentificationTypeDropdown,
  } = useDocumentIdentificationType();
  const {
    fetchDepartments,
    fetchMunicipalitiesByDepartment,
    departments,
    municipalities,
  } = useLocation();

  // ⚡ Cargar combos cuando se abre
  useEffect(() => {
    if (isOpen) {
      // No hacemos resetForm porque queremos mantener el form cargado desde loadCustomerForEdit
      fetchEntityTypeDropdown();
      fetchTaxpayerCategoryDropdown();
      fetchEconomicActivitiesDropdown();
      fetchDepartments();
      fetchDocumentIdentificationTypeDropdown();
    }
  }, [isOpen]);

  // ✅ Si no hay form cargado todavía, puedes mostrar un loader o nada
  if (!form || form.id === null) {
    return null;
  }

  const entityTypeId = Number(form.entityTypeId);

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Editar Proveedor"
      maxWidth="max-w-7xl"
    >
      <div className="mt-4 space-y-8">
        {/* 🔹 Tipo de Entidad */}
        <div>
          <div className="mb-6 border-b pb-4">
            <h2 className="text-gray-800 dark:text-gray-200 mb-1 text-xl font-bold">
              Tipo de Entidad
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Este proveedor fue registrado con el tipo de entidad que se
              muestra a continuación. Por razones de integridad de datos, no es
              posible modificar este campo.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <SelectField
              iconKey="list"
              label="Tipo de Entidad"
              name="entityTypeId"
              value={form.entityTypeId ?? ''}
              disabled
              onChange={() => {}} // opcional: noop
              options={entityTypeDropdown
                .filter((t) => t.id !== 3)
                .map((t) => ({
                  label: t.name,
                  value: t.id,
                }))}
            />
          </div>
        </div>

        {/* 🔹 Datos dinámicos según tipo */}
        <div>
          <div className="mb-6 border-b pb-4">
            <h2 className="text-gray-800 dark:text-gray-200 mb-1 text-xl font-bold">
              Datos del Proveedor
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Actualiza la información necesaria y guarda los cambios.
            </p>
          </div>

          {entityTypeId === 1 && (
            <SupplierNaturalForm
              form={form}
              updateField={updateFormField}
              taxpayerCategoryDropdown={taxpayerCategoryDropdown}
              economicActivitiesDropdown={economicActivitiesDropdown}
              departments={departments}
              municipalities={municipalities}
              documentIdentificationTypeDropdown={
                documentIdentificationTypeDropdown
              }
              onFetchMunicipalities={fetchMunicipalitiesByDepartment}
              onCancel={onClose}
              onSubmit={async () => {
                await saveSupplier(); // ya detecta que es edición porque hay id
                onSubmit?.();
              }}
              isSubmitting={isSubmitting}
            />
          )}

          {entityTypeId === 2 && (
            <SupplierJuridicoForm
              form={form}
              updateField={updateFormField}
              taxpayerCategoryDropdown={taxpayerCategoryDropdown}
              economicActivitiesDropdown={economicActivitiesDropdown}
              departments={departments}
              municipalities={municipalities}
              documentIdentificationTypeDropdown={
                documentIdentificationTypeDropdown
              }
              onFetchMunicipalities={fetchMunicipalitiesByDepartment}
              onCancel={onClose}
              onSubmit={async () => {
                await saveSupplier();
                onSubmit?.();
              }}
              isSubmitting={isSubmitting}
            />
          )}

          {entityTypeId === 4 && (
            <SupplierSubjectExcludedForm
              form={form}
              updateField={updateFormField}
              departments={departments}
              municipalities={municipalities}
              documentIdentificationTypeDropdown={
                documentIdentificationTypeDropdown
              }
              onFetchMunicipalities={fetchMunicipalitiesByDepartment}
              onCancel={onClose}
              onSubmit={async () => {
                await saveSupplier();
                onSubmit?.();
              }}
              isSubmitting={isSubmitting}
            />
          )}
        </div>
      </div>
    </FormModal>
  );
};

export default SupplierEditPopup;
